/**
 * <s-product-rail> fills a horizontal product row on the product page.
 *
 * data-source="recommendations": fetches Shopify product recommendations
 *   (data-url = routes.product_recommendations_url, data-intent = related | complementary).
 * data-offset / data-fetch-limit: skip the first N recommendations (so two rails can share one
 *   intent without repeating products; the API returns at most 10).
 * data-source="recent": products this browser viewed, stored in localStorage
 *   under "ksv:recently-viewed"; the current product (data-handle) is recorded
 *   and excluded from the row.
 *
 * Cards are cloned from the <template data-card> inside the element so every
 * class lives in Liquid (and is compiled by Tailwind). Slots are filled via
 * [data-slot] attributes. The whole rail stays hidden when no products return.
 */
const RECENT_KEY = 'ksv:recently-viewed';
const RECENT_MAX = 12;

function readRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecent(handle) {
  if (!handle) return;
  try {
    const list = readRecent().filter((h) => h !== handle);
    list.unshift(handle);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* storage unavailable: the recent row simply stays hidden */
  }
}

function formatMoney(cents, format) {
  const value = Number(cents) / 100;
  const fmt = format || '{{amount}}';
  const withSeparators = (num, decimals, thousands, decimal) => {
    const fixed = num.toFixed(decimals);
    const [whole, frac] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? `${grouped}${decimal}${frac}` : grouped;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return withSeparators(value, 0, ',', '.');
      case 'amount_with_comma_separator': return withSeparators(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withSeparators(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withSeparators(value, 2, "'", '.');
      default: return withSeparators(value, 2, ',', '.');
    }
  });
}

function imageUrl(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  try {
    const u = new URL(url, location.origin);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch {
    return url;
  }
}

class SProductRail extends HTMLElement {
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const source = this.dataset.source;
    if (source === 'recent') {
      const current = this.dataset.handle;
      const handles = readRecent().filter((h) => h !== current).slice(0, 8);
      writeRecent(current);
      if (handles.length) this.#loadRecent(handles);
    } else {
      this.#loadRecommendations();
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #loadRecommendations() {
    const url = new URL(this.dataset.url, location.origin);
    if (!url.pathname.endsWith('.json')) url.pathname += '.json';
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', this.dataset.fetchLimit || this.dataset.limit || '8');
    url.searchParams.set('intent', this.dataset.intent || 'related');
    try {
      const res = await fetch(url, { signal: this.#controller.signal });
      if (!res.ok) return;
      const data = await res.json();
      let products = data.products || [];
      const fallback = this.dataset.fallbackIntent;
      if (!products.length && fallback && fallback !== url.searchParams.get('intent')) {
        url.searchParams.set('intent', fallback);
        const retry = await fetch(url, { signal: this.#controller.signal });
        if (retry.ok) products = (await retry.json()).products || [];
      }
      const offset = Number(this.dataset.offset || 0);
      if (offset) products = products.slice(offset);
      this.#render(products);
    } catch {
      /* network or abort: keep the rail hidden */
    }
  }

  async #loadRecent(handles) {
    const root = window.Shopify?.routes?.root || '/';
    const results = await Promise.all(handles.map(async (handle) => {
      try {
        const res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal: this.#controller.signal });
        return res.ok ? res.json() : null;
      } catch {
        return null;
      }
    }));
    this.#render(results.filter(Boolean));
  }

  #render(products) {
    const template = this.querySelector('template[data-card]');
    const track = this.querySelector('[data-track]');
    if (!template || !track || !products.length) return;
    const format = this.dataset.moneyFormat;
    const discountLabel = this.dataset.discountLabel || '-{percent}%';
    const fragment = document.createDocumentFragment();

    for (const product of products) {
      if (product.available === false && this.dataset.source !== 'recent') continue;
      const card = template.content.firstElementChild.cloneNode(true);
      const url = product.url || `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;
      card.querySelectorAll('[data-slot="link"]').forEach((a) => { a.href = url; });
      const img = card.querySelector('[data-slot="image"]');
      const src = product.featured_image || product.images?.[0];
      if (img && src) {
        img.src = imageUrl(src, 500);
        img.srcset = `${imageUrl(src, 300)} 300w, ${imageUrl(src, 500)} 500w, ${imageUrl(src, 700)} 700w`;
        img.alt = product.title;
      } else if (img) {
        img.remove();
      }
      const vendor = card.querySelector('[data-slot="vendor"]');
      if (vendor) vendor.textContent = product.vendor || '';
      const title = card.querySelector('[data-slot="title"]');
      if (title) title.textContent = product.title;
      const price = card.querySelector('[data-slot="price"]');
      if (price) price.textContent = formatMoney(product.price, format);
      const compareAt = Number(product.compare_at_price || 0);
      const compare = card.querySelector('[data-slot="compare"]');
      const badge = card.querySelector('[data-slot="badge"]');
      if (compareAt > Number(product.price)) {
        if (compare) { compare.textContent = formatMoney(compareAt, format); compare.hidden = false; }
        const percent = Math.round(((compareAt - product.price) / compareAt) * 100);
        if (badge && percent > 0) { badge.textContent = discountLabel.replace('{percent}', percent); badge.hidden = false; }
      }
      // Optional quick-add form (cart drawer): single-variant products add directly,
      // multi-variant products show a "choose options" link instead.
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variantInput = card.querySelector('[data-slot="variant"]');
      const formWrap = card.querySelector('[data-slot="form-wrap"]');
      const choose = card.querySelector('[data-slot="choose"]');
      if (variantInput && formWrap) {
        const firstAvailable = variants.find((v) => v.available);
        if (variants.length === 1 && firstAvailable) {
          variantInput.value = String(firstAvailable.id);
          if (choose) choose.remove();
        } else {
          formWrap.remove();
          if (choose) { choose.hidden = false; choose.href = url; }
        }
      }
      fragment.appendChild(card);
    }

    if (!fragment.childNodes.length) return;
    track.replaceChildren(fragment);
    this.hidden = false;
  }
}

if (!customElements.get('s-product-rail')) customElements.define('s-product-rail', SProductRail);
